import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, MessageSquare, Send, Settings, History, CheckCircle2,
  XCircle, AlertTriangle, Clock, Slack, Users, TestTube,
  DollarSign, TrendingUp, Shield, Zap, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Use /api/alerts for the new notification center
const ALERTS_API = `${API_URL}/alerts`;

// Microsoft Teams icon component
const TeamsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.5 6.5h-3v-2a2 2 0 0 0-2-2h-5a2 2 0 0 0-2 2v2h-3a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h15a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-10-2h5v2h-5v-2zm10 13h-15v-9h15v9z"/>
    <circle cx="12" cy="13" r="2"/>
    <path d="M16 13a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/>
  </svg>
);

const NotificationCenter = () => {
  const { shouldShowData } = useDemoData();

  const [settings, setSettings] = useState({
    slack_enabled: true,
    teams_enabled: true,
    anomaly_alerts: true,
    budget_alerts: true,
    daily_summary: true,
    weekly_report: true,
    threshold_alerts: true,
    cost_threshold: 1000,
    anomaly_threshold: 20
  });
  const [integrations, setIntegrations] = useState({ slack: {}, teams: {} });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({ slack: false, teams: false });

  useEffect(() => {
    fetchSettings();
    fetchHistory();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${ALERTS_API}/settings`);
      if (response.data?.settings) setSettings((prev) => ({ ...prev, ...response.data.settings }));
      if (response.data?.integrations) setIntegrations((prev) => ({ ...prev, ...response.data.integrations }));
    } catch (error) {
      // Endpoint may be unavailable — keep sensible defaults so UI never breaks.
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${ALERTS_API}/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(`${ALERTS_API}/settings`, newSettings);
      setSettings(newSettings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const testSlack = async () => {
    setTesting({ ...testing, slack: true });
    try {
      await axios.post(`${ALERTS_API}/test/slack`);
      toast.success('Test notification sent to Slack!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Slack notification');
    } finally {
      setTesting({ ...testing, slack: false });
    }
  };

  const testTeams = async () => {
    setTesting({ ...testing, teams: true });
    try {
      await axios.post(`${ALERTS_API}/test/teams`);
      toast.success('Test notification sent to Teams!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Teams notification');
    } finally {
      setTesting({ ...testing, teams: false });
    }
  };

  const sendDailySummary = async () => {
    try {
      await axios.post(`${ALERTS_API}/summary/daily`);
      toast.success('Daily summary sent!');
      fetchHistory();
    } catch (error) {
      toast.error('Failed to send daily summary');
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${ALERTS_API}/history`);
      setHistory([]);
      toast.success('History cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const toggleSetting = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    updateSettings(newSettings);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Notification Center" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="notification-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-3">
            <Bell className="w-7 h-7 text-emerald-500" />
            Notification Center
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Configure Slack and Teams alerts for cost anomalies and budget warnings
          </p>
        </div>
        <Button onClick={sendDailySummary} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
          <Send className="w-4 h-4" />
          Send Daily Summary
        </Button>
      </div>

      <Tabs defaultValue="integrations">
        <TabsList>
          <TabsTrigger value="integrations" className="gap-2">
            <MessageSquare className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alert Settings
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Slack Integration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 dark:bg-cyan-900/30 rounded-lg">
                      <Slack className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Slack</CardTitle>
                      <CardDescription>Send alerts to Slack channels</CardDescription>
                    </div>
                  </div>
                  <Badge className={integrations.slack?.configured 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-slate-100 text-slate-700'
                  }>
                    {integrations.slack?.status || 'Not configured'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Enable Slack notifications</span>
                  <Switch
                    checked={settings.slack_enabled}
                    onCheckedChange={() => toggleSetting('slack_enabled')}
                    disabled={!integrations.slack?.configured}
                  />
                </div>
                
                {!integrations.slack?.configured && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm">
                    <p className="text-amber-800 dark:text-amber-200">
                      Set <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">SLACK_WEBHOOK_URL</code> in your environment to enable Slack notifications.
                    </p>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={testSlack}
                  disabled={!integrations.slack?.configured || testing.slack}
                >
                  {testing.slack ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4" />
                  )}
                  Send Test Message
                </Button>
              </CardContent>
            </Card>

            {/* Teams Integration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 dark:bg-blue-900/30 rounded-lg">
                      <TeamsIcon />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Microsoft Teams</CardTitle>
                      <CardDescription>Send alerts to Teams channels</CardDescription>
                    </div>
                  </div>
                  <Badge className={integrations.teams?.configured 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-slate-100 text-slate-700'
                  }>
                    {integrations.teams?.status || 'Not configured'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Enable Teams notifications</span>
                  <Switch
                    checked={settings.teams_enabled}
                    onCheckedChange={() => toggleSetting('teams_enabled')}
                    disabled={!integrations.teams?.configured}
                  />
                </div>
                
                {!integrations.teams?.configured && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm">
                    <p className="text-amber-800 dark:text-amber-200">
                      Set <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">TEAMS_WEBHOOK_URL</code> in your environment to enable Teams notifications.
                    </p>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={testTeams}
                  disabled={!integrations.teams?.configured || testing.teams}
                >
                  {testing.teams ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4" />
                  )}
                  Send Test Message
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-2">Slack Webhook Setup</h4>
                  <ol className="text-sm text-slate-400 dark:text-slate-400 space-y-1 list-decimal list-inside">
                    <li>Go to api.slack.com/apps</li>
                    <li>Create a new app or select existing</li>
                    <li>Enable "Incoming Webhooks"</li>
                    <li>Add webhook to your channel</li>
                    <li>Copy the webhook URL to SLACK_WEBHOOK_URL</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-2">Teams Webhook Setup</h4>
                  <ol className="text-sm text-slate-400 dark:text-slate-400 space-y-1 list-decimal list-inside">
                    <li>Open Microsoft Teams</li>
                    <li>Go to the channel for alerts</li>
                    <li>Click ••• → Connectors</li>
                    <li>Add "Incoming Webhook"</li>
                    <li>Copy the webhook URL to TEAMS_WEBHOOK_URL</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alert Settings Tab */}
        <TabsContent value="alerts" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Alert Types</CardTitle>
              <CardDescription>Choose which alerts to receive</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium">Anomaly Alerts</p>
                    <p className="text-sm text-slate-400">Notify when unusual spending is detected</p>
                  </div>
                </div>
                <Switch
                  checked={settings.anomaly_alerts}
                  onCheckedChange={() => toggleSetting('anomaly_alerts')}
                />
              </div>
              
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="font-medium">Budget Alerts</p>
                    <p className="text-sm text-slate-400">Notify when budgets reach warning thresholds</p>
                  </div>
                </div>
                <Switch
                  checked={settings.budget_alerts}
                  onCheckedChange={() => toggleSetting('budget_alerts')}
                />
              </div>
              
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="font-medium">Daily Summary</p>
                    <p className="text-sm text-slate-400">Receive daily cost overview at 9 AM</p>
                  </div>
                </div>
                <Switch
                  checked={settings.daily_summary}
                  onCheckedChange={() => toggleSetting('daily_summary')}
                />
              </div>
              
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="font-medium">Weekly Report</p>
                    <p className="text-sm text-slate-400">Receive weekly cost analysis on Monday</p>
                  </div>
                </div>
                <Switch
                  checked={settings.weekly_report}
                  onCheckedChange={() => toggleSetting('weekly_report')}
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-rose-500" />
                  <div>
                    <p className="font-medium">Threshold Alerts</p>
                    <p className="text-sm text-slate-400">Alert when single resource exceeds threshold</p>
                  </div>
                </div>
                <Switch
                  checked={settings.threshold_alerts}
                  onCheckedChange={() => toggleSetting('threshold_alerts')}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Thresholds</CardTitle>
              <CardDescription>Configure alert sensitivity</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cost Threshold ($)</label>
                  <Input
                    type="number"
                    value={settings.cost_threshold}
                    onChange={(e) => updateSettings({ ...settings, cost_threshold: parseFloat(e.target.value) || 0 })}
                    placeholder="1000"
                  />
                  <p className="text-xs text-slate-400">Alert when single resource exceeds this amount</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Anomaly Threshold (%)</label>
                  <Input
                    type="number"
                    value={settings.anomaly_threshold}
                    onChange={(e) => updateSettings({ ...settings, anomaly_threshold: parseFloat(e.target.value) || 0 })}
                    placeholder="20"
                  />
                  <p className="text-xs text-slate-400">Alert when cost exceeds expected by this percentage</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-slate-900 dark:text-white">Recent Notifications</h3>
            {history.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearHistory}>
                Clear History
              </Button>
            )}
          </div>

          {history.length === 0 ? (
            <Card className="p-8 text-center">
              <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-400">No notification history yet</p>
              <p className="text-sm text-slate-400 mt-1">Alerts will appear here when sent</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {history.map((item, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${
                      item.type === 'anomaly' ? 'bg-amber-100 dark:bg-amber-900/30' :
                      item.type === 'budget' ? 'bg-emerald-100 dark:bg-blue-900/30' :
                      'bg-emerald-100 dark:bg-emerald-900/30'
                    }`}>
                      {item.type === 'anomaly' && <AlertTriangle className="w-5 h-5 text-amber-600" />}
                      {item.type === 'budget' && <Shield className="w-5 h-5 text-emerald-600" />}
                      {item.type === 'summary' && <TrendingUp className="w-5 h-5 text-emerald-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium capitalize">{item.type} Alert</span>
                        <Badge variant="outline" className="text-xs">
                          {new Date(item.timestamp).toLocaleString()}
                        </Badge>
                      </div>
                      {item.alert && (
                        <p className="text-sm text-slate-400 dark:text-slate-400">
                          {item.type === 'anomaly' && `${item.alert.service}: $${item.alert.current_cost} (expected $${item.alert.expected_cost})`}
                          {item.type === 'budget' && `${item.alert.budget_name}: ${item.alert.percentage}% utilized`}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1 text-xs">
                          <Slack className="w-3 h-3" />
                          {item.results?.slack ? (
                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <XCircle className="w-3 h-3 text-slate-400" />
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <TeamsIcon />
                          {item.results?.teams ? (
                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <XCircle className="w-3 h-3 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NotificationCenter;
